import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { adaptEchartsOptions, watchBodyDarkMode$ } from '@examdojo/core/widget';
import type { EChartsOption } from 'echarts';
import { BarChart, LineChart, ScatterChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent, TitleComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([LineChart, BarChart, ScatterChart, SVGRenderer, GridComponent, MarkLineComponent, TitleComponent]);

import type { TestMeasureId } from '../test-measures/test-measures.model';
import type { TestResultOrError } from './test-runner.model';
import { isTestError } from './test-runner.model';
import { buildTestResultChartOptions } from './test-result-chart.util';

@Component({
  selector: 'dojo-test-runner',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './test-runner.component.html',
  styleUrl: './test-runner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestRunnerComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;

  readonly result = input.required<TestResultOrError>();
  readonly highlightedMeasure = input<TestMeasureId | null>(null);

  readonly isError = computed(() => isTestError(this.result()));

  readonly errorMessage = computed(() => {
    const r = this.result();
    return isTestError(r) ? r.error : null;
  });

  readonly testResult = computed(() => {
    const r = this.result();
    return isTestError(r) ? null : r;
  });

  readonly initOpts = { renderer: 'svg' } as const;

  readonly chartOptions = computed<EChartsOption | null>(() => {
    const r = this.testResult();
    const highlight = this.highlightedMeasure();
    return r ? buildTestResultChartOptions(r, highlight) : null;
  });

  readonly adaptedOptions = computed<EChartsOption | null>(() => {
    const raw = this.chartOptions();
    return raw ? adaptEchartsOptions(raw, this.document, this.isDark()) : null;
  });
}
